<template>
  <div class="user-dashboard">
    <!-- Dashboard Style Topbar Header -->
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon" />
          <span v-html="$t('mainuser.pageTitle')"></span>
        </h4>

        <div class="dashboard-header-right">
          <button class="report-btn hero-action" @click="$router.push('/admin/new-request')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('mainuser.createNewRequest') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 4 Key Stat Cards (Attached KPI Grid) -->
    <HomeStatCards :cards="statCards" :active-key="selectedStatus" @select="handleCardSelect" />

    <CCard class="details-card mb-4">
      <CCardHeader class="details-card-header"><h4 class="mb-0">{{ activeTableTitle }}</h4></CCardHeader>
      <CCardBody>
        <Ureportable
          ref="activeReportTable"
          mode="unfinished"
          ticket-source="admin"
          :status-filter="selectedStatus"
          :date-range="selectedRange"
          :start-date="startDate"
          :end-date="endDate"
          :search-query="searchQuery"
          @tickets-loaded="updateStats"
          @show-detail="handleShowDetail"
        />
      </CCardBody>
    </CCard>

    <TicketDetailModal v-if="selectedReport" @close="selectedReport=null"><ReportDetail :report="selectedReport" @close="selectedReport=null" @refresh="refreshTables" @update:report="selectedReport = $event" /></TicketDetailModal>
  </div>
</template>

<script>
import HomeStatCards from '@/components/common/HomeStatCards.vue'
import Ureportable from '@/components/user/Ureportable.vue'
import ReportDetail from './ReportDetail.vue'
import TicketDetailModal from '@/components/common/TicketDetailModal.vue'

export default {
  name: 'AdminHome',
  components: { HomeStatCards, Ureportable, ReportDetail, TicketDetailModal },
  data: () => ({
    selectedReport: null,
    selectedStatus: 'unassigned',
    selectedRange: 'all',
    startDate: '',
    endDate: '',
    searchQuery: '',
    showSearchDropdown: false,
    currentMatchIndex: 0,
    loadedTickets: [],
    stats: {
      unassigned: 0,
      inProgress: 0,
      overSla: 0,
      completed: 0,
    },
  }),
  computed: {
    rangeOptions() {
      return [
        { label: this.dashboardText("สัปดาห์", "Week"), value: "week" },
        { label: this.dashboardText("เดือน", "Month"), value: "month" },
        { label: this.dashboardText("ปี", "Year"), value: "year" },
        { label: "", value: "custom" },
      ];
    },
    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return [];
      const q = this.searchQuery.trim().toLowerCase();
      const results = [];
      this.loadedTickets.forEach((ticket) => {
        const ticketNo = String(ticket.ticketNumber || ticket._id || "").toLowerCase();
        const reporter = String(ticket.userPart?.name || ticket.userPart?.username || "").toLowerCase();
        const prob = String(ticket.userPart?.problemDetails || "").toLowerCase();
        const dept = String(ticket.userPart?.category?.departmentName || ticket.userPart?.category?.name || ticket.userPart?.category || "").toLowerCase();
        const cat = String(ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory || "").toLowerCase();
        const status = String(ticket.status || "").toLowerCase();

        if (ticketNo.includes(q) || reporter.includes(q) || prob.includes(q) || dept.includes(q) || cat.includes(q) || status.includes(q)) {
          results.push({
            id: 'ticket-' + (ticket._id || ticket.ticketNumber),
            ticket,
            sectionLabel: this.dashboardText('ใบแจ้งซ่อม', 'Ticket'),
            badgeClass: 'badge-ticket',
            title: `#${ticket.ticketNumber || '-'} — ${ticket.userPart?.name || this.dashboardText('ผู้แจ้ง', 'Reporter')}`,
            description: ticket.userPart?.problemDetails || '-'
          });
        }
      });
      return results;
    },
    activeTableTitle() {
      const isEn = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase().startsWith('en');
      const baseTitle = this.$t('mainuser.activeRequestLabel') || (isEn ? 'Maintenance Requests' : 'รายการคำขอแจ้งซ่อม');
      const suffixMap = {
        unassigned: isEn ? 'Unassigned' : 'รอมอบหมาย',
        active: isEn ? 'In Progress' : 'กำลังดำเนินการ',
        overdue: isEn ? 'Overdue SLA' : 'งานที่เกินกำหนด SLA',
        completed: isEn ? 'Completed' : 'ปิดงานแล้ว',
        all: isEn ? 'All' : 'ทั้งหมด',
      };
      const suffix = suffixMap[this.selectedStatus];
      return suffix ? `${baseTitle} — ${suffix}` : baseTitle;
    },
    statCards() {
      return [
        {
          key: 'unassigned',
          title: this.$t("home.kpiUnassigned") || "รอมอบหมาย",
          value: this.stats.unassigned,
          icon: "cil-bell",
          iconClass: "icon-blue",
          badge: "PENDING",
          badgeClass: "trend-neutral",
        },
        {
          key: 'active',
          title: this.$t("dashboard.kpiInProgress") || "กำลังดำเนินการ",
          value: this.stats.inProgress,
          icon: "cil-av-timer",
          iconClass: "icon-orange",
          badge: "ACTIVE",
          badgeClass: "trend-orange",
        },
        {
          key: 'overdue',
          title: this.dashboardText('งานที่เกินกำหนด SLA', 'Overdue SLA Jobs'),
          value: this.stats.overSla,
          icon: "cil-clock",
          iconClass: "icon-red",
          badge: "OVER SLA",
          badgeClass: this.stats.overSla > 0 ? "trend-down" : "trend-up",
        },
        {
          key: 'completed',
          title: this.$t("dashboard.kpiCompleted") || "ปิดงานแล้ว",
          value: this.stats.completed,
          icon: "cil-check-circle",
          iconClass: "icon-green",
          badge: "DONE",
          badgeClass: "trend-up",
        },
      ];
    },
  },
  mounted() {
    this.syncRouteQuery();
    window.addEventListener('keydown', this.handleGlobalKeydown);
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.syncRouteQuery();
      }
    }
  },
  methods: {
    dashboardText(thText, enText) {
      const locale = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase();
      return locale.startsWith('en') ? enText : thText;
    },

    selectRange(range) {
      this.selectedRange = range;
      if (range !== 'custom') {
        this.startDate = '';
        this.endDate = '';
      }
    },
    onCustomDateChange() {
      if (this.startDate && this.endDate) {
        this.selectedRange = 'custom';
      }
    },
    nextMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    prevMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    jumpToMatch(idx) {
      if (!this.searchMatches[idx]) return;
      this.currentMatchIndex = idx;
      const match = this.searchMatches[idx];
      this.showSearchDropdown = false;
      if (match.ticket) {
        this.handleShowDetail(match.ticket);
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.showSearchDropdown = false;
      this.currentMatchIndex = 0;
    },
    closeSearchDropdown() {
      this.showSearchDropdown = false;
    },
    handleGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        const input = this.$refs.searchBoxRef?.querySelector('input');
        if (input) {
          e.preventDefault();
          input.focus();
          input.select();
          this.showSearchDropdown = true;
        }
      }
    },
    handleClickOutside(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false;
      }
    },

    syncRouteQuery() {
      const q = this.$route.query.filter || this.$route.query.status || this.$route.query.view;
      if (q) {
        const val = String(q).toLowerCase();
        if (['unassigned', 'pending'].includes(val)) {
          this.selectedStatus = 'unassigned';
        } else if (['active', 'in_progress'].includes(val)) {
          this.selectedStatus = 'active';
        } else if (['overdue', 'over_sla'].includes(val)) {
          this.selectedStatus = 'overdue';
        } else if (['completed', 'done', 'closed'].includes(val)) {
          this.selectedStatus = 'completed';
        } else if (['all', 'total'].includes(val)) {
          this.selectedStatus = 'all';
        }
      }
      if (this.$route.query.ticketId || this.$route.query.ticketNo) {
        this.selectedRange = 'all';
        this.$nextTick(() => {
          this.$refs.activeReportTable?.jumpToTicket(this.$route.query.ticketId, this.$route.query.ticketNo);
        });
      }
    },
    handleCardSelect(key) {
      this.selectedStatus = key;
      const currentQuery = this.$route.query.filter || this.$route.query.status || this.$route.query.view;
      if (currentQuery !== key) {
        this.$router.replace({ path: '/admin/mainadmin', query: { filter: key } }).catch(() => {});
      }
    },
    updateStats(tickets) {
      const source = Array.isArray(tickets) ? tickets : [];
      this.loadedTickets = source;
      const unassigned = source.filter(ticket => ticket.status === "pending" || (ticket.status === "assigned" && !ticket.userPart?.category)).length;
      const inProgress = source.filter(ticket => (ticket.status === "assigned" && Boolean(ticket.userPart?.category)) || ticket.status === "in_progress").length;
      const overSla = source.filter(ticket => this.isTicketOverdue(ticket)).length;
      const completed = source.filter(ticket => ["done", "welldone"].includes(ticket.status)).length;
      if (this.selectedReport && this.selectedReport._id) {
        const matching = source.find(t => String(t._id) === String(this.selectedReport._id));
        if (matching) {
          this.selectedReport = { ...this.selectedReport, ...matching };
        }
      }
      this.stats = { unassigned, inProgress, overSla, completed };

      if (this.$route.query && (this.$route.query.ticketId || this.$route.query.ticketNo)) {
        this.$nextTick(() => {
          this.$refs.activeReportTable?.jumpToTicket(this.$route.query.ticketId, this.$route.query.ticketNo);
        });
      }
    },
    isTicketOverdue(ticket) {
      if (!ticket || ['cancelled'].includes(ticket.status)) return false;
      if (ticket.slaDeadline) {
        const end = ['done', 'welldone'].includes(ticket.status)
          ? new Date(ticket.technicianPart?.dateTimeFinished || ticket.updatedAt)
          : new Date();
        return end.getTime() > new Date(ticket.slaDeadline).getTime();
      }
      return false;
    },
    handleShowDetail(item) {
      this.selectedReport = { ...item }
      this.$nextTick(() => document.getElementById('admin-home-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    },
    refreshTables(opts) {
      if (opts && opts.updatedTicket && this.selectedReport) {
        this.selectedReport = { ...this.selectedReport, ...opts.updatedTicket };
      }
      if (!opts || opts.keepOpen !== true) {
        this.selectedReport = null;
      }
      if (this.$refs.activeReportTable) this.$refs.activeReportTable.loadTickets()
    },
  },
}
</script>

<style scoped>
/* ===== Dashboard Topbar & Header ===== */
.user-dashboard {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.dashboard-sticky-area {
  position: sticky !important;
  top: 76px !important;
  z-index: 1020 !important;
  margin: 0 0 20px 0 !important;
  padding: 0 0 6px 0 !important;
  width: 100%;
  background: #ebedef !important;
}

.dashboard-topbar {
  width: 100%;
  min-width: 0;
  height: 112px !important;
  min-height: 112px !important;
  margin: 0 !important;
  padding: 22px 28px !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 16px !important;
  flex-wrap: nowrap !important;
  background: #1e40af !important;
  border: 0 !important;
  border-radius: 12px !important;
  box-sizing: border-box !important;
  color: #fff !important;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  color: #ffffff !important;
  font-size: 1.6rem !important;
  font-weight: 800 !important;
  letter-spacing: -0.02em !important;
  line-height: 1.2 !important;
  min-width: 0 !important;
  flex: 1 1 auto !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.dashboard-title-icon,
.dashboard-page-title svg,
::v-deep .dashboard-page-title .c-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 54px !important;
  height: 54px !important;
  flex: 0 0 54px !important;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.16) !important;
  color: #ffffff !important;
  padding: 11px !important;
  margin: 0 !important;
}

.dashboard-filter {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
}

.report-btn,
.hero-action {
  background: #ffffff !important;
  border: none !important;
  color: #1e40af !important;
  min-height: 48px !important;
  padding: 10px 24px !important;
  border-radius: 50px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
}

.report-btn:hover,
.hero-action:hover {
  background: #fdfdfd !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.details-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.06);
}

.details-card-header {
  background: #f8fafc !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.08) !important;
  color: #1e40af !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.1);
  padding: 18px 22px;
}

.details-card-header h4 {
  margin-bottom: 0;
  color: #1e40af;
  font-weight: 800;
}

::v-deep .card-btn {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.dashboard-header-right {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-end !important;
  width: auto !important;
  flex-shrink: 0 !important;
  margin-left: auto !important;
}

@media (max-width: 1200px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 16px 0 !important;
    padding: 4px 0 6px 0 !important;
    background: #ebedef !important;
  }
}

@media (max-width: 768px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 2px 0 6px 0 !important;
    background: #ebedef !important;
  }
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 88px !important;
    padding: 14px 18px !important;
    border-radius: 12px !important;
    gap: 12px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.3rem !important;
    gap: 12px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 46px !important;
    height: 46px !important;
    flex: 0 0 46px !important;
    border-radius: 13px !important;
    padding: 9px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 44px !important;
    padding: 8px 18px !important;
    font-size: 13.5px !important;
    gap: 6px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
  .details-card-header { padding: 14px 16px; }
  .details-card-header h4 { font-size: 1rem; }
  .details-card .card-body { padding: 12px; }
}

@media (max-width: 480px) {
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 84px !important;
    padding: 12px 16px !important;
    border-radius: 12px !important;
    gap: 10px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.15rem !important;
    gap: 10px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 42px !important;
    height: 42px !important;
    flex: 0 0 42px !important;
    border-radius: 12px !important;
    padding: 8px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 40px !important;
    padding: 7px 14px !important;
    font-size: 13px !important;
    gap: 5px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
}
</style>
